import { Shield, Users, Radio, LogIn, HandHeart, Newspaper, Menu, Crown, Trophy, ImagePlus, X, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { UserMenu } from "@/components/UserMenu";
import { useAuth } from "@/contexts/AuthContext";
import { AUTH_DISABLED, NEWS_ENABLED } from "@/lib/featureFlags";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState } from "react";
import logo from "@/assets/logo-icon-96.png";

const NAV_ITEMS = [
  { path: "/live", label: "Live", icon: Radio, requiresAuth: false, adminOnly: false },
  { path: "/compare", label: "Compare", icon: Users, requiresAuth: false, adminOnly: false },
  { path: "/sack-race", label: "Sack Race", icon: Flame, requiresAuth: false, adminOnly: false, description: "Current hot seats" },
  { path: "/rankings", label: "Rankings", icon: Trophy, requiresAuth: false, adminOnly: false, description: "All-time overachievers" },
  { path: "/news", label: "Changes", icon: Newspaper, requiresAuth: false, adminOnly: false, enabled: NEWS_ENABLED },
  { path: "/create", label: "Create", icon: ImagePlus, requiresAuth: true, adminOnly: false },
  { path: "/contribute", label: "Contribute", icon: HandHeart, requiresAuth: true, adminOnly: false },
  { path: "/admin", label: "Admin", icon: Shield, requiresAuth: true, adminOnly: true },
];

export function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, isLoading, isPremium, viewAsRole, setViewAsRole, actualIsAdmin } = useAuth();
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  const handleNavigation = (path: string) => {
    navigate(path);
    setSheetOpen(false);
  };

  const visibleNavItems = NAV_ITEMS.filter(item => {
    if ((item as { enabled?: boolean }).enabled === false) return false;
    if (AUTH_DISABLED && (item.requiresAuth || item.adminOnly)) return false;
    if (item.adminOnly && !isAdmin) return false;
    if (item.requiresAuth && !user) return false;
    return true;
  });

  // Desktop navigation
  const DesktopNav = () => (
    <div className="hidden md:flex items-center gap-1">
      {visibleNavItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.path);
        return (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            title={(item as { description?: string }).description}
            className={`relative flex items-center gap-1.5 px-3 py-2 text-sm font-medium tracking-wide uppercase transition-colors ${
              active
                ? "text-chrome-foreground"
                : "text-chrome-muted hover:text-chrome-foreground"
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
            <span>{item.label}</span>
            <span
              className={`absolute left-3 right-3 -bottom-[1px] h-[2px] rounded-full bg-brand transition-transform duration-200 origin-left ${
                active ? "scale-x-100" : "scale-x-0"
              }`}
            />
          </button>
        );
      })}

      <div className="mx-2 h-5 w-px bg-chrome-border" />

      <ThemeToggle />

      {!AUTH_DISABLED && user && !isLoading && !isPremium && (
        <Button
          variant="outline"
          onClick={() => navigate("/pricing")}
          className="gap-1.5 border-brand/50 bg-transparent text-brand hover:bg-brand/10 hover:text-brand"
          size="sm"
        >
          <Crown className="h-3.5 w-3.5" />
          <span>Upgrade</span>
        </Button>
      )}

      {!AUTH_DISABLED && !isLoading && (
        user ? (
          <UserMenu />
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/auth")}
            className="gap-2 text-chrome-muted hover:bg-chrome-border/60 hover:text-chrome-foreground"
          >
            <LogIn className="h-4 w-4" />
            <span>Sign In</span>
          </Button>
        )
      )}
    </div>
  );


  // Mobile hamburger menu
  const MobileNav = () => (
    <div className="flex md:hidden items-center gap-1">
      <ThemeToggle />

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 text-chrome-foreground hover:bg-chrome-border/60 hover:text-chrome-foreground"
          >
            <Menu className="h-5 w-5" />
            <span className="sr-only">Open menu</span>
          </Button>
        </SheetTrigger>

        <SheetContent side="right" className="w-64">
          <nav className="flex flex-col gap-1 mt-8">
            {visibleNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`flex w-full items-center gap-3 border-l-2 px-3 py-3 text-left text-base font-display font-semibold uppercase tracking-wide transition-colors ${
                    active
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="flex flex-col">
                    <span>{item.label}</span>
                    {(item as { description?: string }).description && (
                      <span className="font-sans text-xs font-normal normal-case tracking-normal text-muted-foreground">
                        {(item as { description?: string }).description}
                      </span>
                    )}
                  </span>
                </button>
              );
            })}

            
            {!AUTH_DISABLED && (
              <>
                <div className="border-t border-border my-2" />
                
                {!isLoading && (
                  user ? (
                    <div className="px-2">
                      <UserMenu />
                    </div>
                  ) : (
                    <Button 
                      variant="ghost" 
                      onClick={() => handleNavigation("/auth")} 
                      className="w-full justify-start gap-3"
                    >
                      <LogIn className="h-5 w-5" />
                      <span>Sign In</span>
                    </Button>
                  )
                )}
              </>
            )}
          </nav>
        </SheetContent>
      </Sheet>
    </div>
  );

  const viewAsLabel = viewAsRole === 'user' ? 'Free User' : viewAsRole === 'contributor' ? 'Contributor' : null;

  return (
    <>
      {actualIsAdmin && viewAsRole && viewAsLabel && (
        <div className="bg-amber-500/90 text-amber-950 text-center text-sm py-1.5 px-4 flex items-center justify-center gap-2">
          <span className="font-medium">Viewing as {viewAsLabel}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-amber-950 hover:bg-amber-600/50 hover:text-amber-950"
            onClick={() => setViewAsRole(null)}
          >
            <X className="h-3.5 w-3.5 mr-1" />
            Exit
          </Button>
        </div>
      )}
      <header className="chrome-band border-b-2 border-brand">
        <div className="container mx-auto px-4">
          <div className="flex h-14 md:h-16 items-center justify-between">
            <Link
              to="/"
              className="flex items-center gap-2 md:gap-2.5 transition-opacity hover:opacity-80"
            >
              <img src={logo} alt="Manager Table" width={96} height={96} className="h-7 w-7 md:h-8 md:w-8" />
              <span className="font-display text-lg md:text-2xl font-extrabold uppercase tracking-[-0.02em] text-chrome-foreground">
                Manager<span className="text-brand">Table</span>
              </span>
            </Link>

            <DesktopNav />
            <MobileNav />
          </div>
        </div>
      </header>

    </>
  );
}
